/**
 * Token Service for FrontEnd React App
 * Handles token retrieval from multiple sources including parent windows, APIs, and storage
 */



class TokenService {
  constructor() {
    this.token = null;
    this.tokenSource = null;
    this.listeners = [];
    this.isInitialized = false;
  }

  /**
   * Initialize token service - tries all methods to get token
   */
  async initialize() {
    if (this.isInitialized) {
      console.log('🔑 TokenService: Already initialized, returning existing token');
      return this.token;
    }

    console.log('🔑 TokenService: Initializing...');
    
    // Try multiple methods in order of preference
    const methods = [
      // { fn: () => this.getTokenFromStorage(), name: 'Storage' },
      // { fn: () => this.getTokenFromCookies(), name: 'Cookies' },
      { fn: () => this.getTokenFromParentPostMessage(), name: 'PostMessage' },
      // { fn: () => this.getTokenFromParentWindow(), name: 'ParentWindow' },
      // { fn: () => this.getTokenFromURL(), name: 'URL' },
      // { fn: () => this.getTokenFromAPI(), name: 'API' },
    ];

    for (const method of methods) {
      try {
        console.log(`🔍 Trying ${method.name}...`);
        const token = await method.fn();
        if (token) {
          console.log(`✅ Token found via ${method.name}`);
          this.setToken(token, method.name);
          break;
        } else {
          console.log(`❌ No token from ${method.name}`);
        }
      } catch (error) {
        console.warn(`❌ TokenService ${method.name} failed:`, error.message);
      }
    }

    this.isInitialized = true;
    
    if (this.token) {
      console.log('🎉 TokenService: Initialization successful');
    } else {
      console.log('⚠️ TokenService: No token found from any source');
    }
    
    return this.token;
  }
  
/**
   * Method 1: Get token via PostMessage from parent window
   */
  getTokenFromParentPostMessage() {
    return new Promise((resolve) => {
      if (window.parent === window) {
        resolve(null);
        return;
      }

      const timeout = setTimeout(() => {
        resolve(null);
      }, 3000); // 3 second timeout

      // Listen for token from parent
      const messageHandler = (event) => {
        // 🔒 SECURITY: Validate origin
        const allowedOrigins = [
          'http://localhost',
          'https://localhost', 
          'http://127.0.0.1',
          'https://127.0.0.1',
          'https://rostering.crystalpayroll.co.nz',
          'https://crystalpayroll.co.nz',
          'https://secure.crystalpayroll.co.nz'
          // Add production origins here
        ];

        const isOriginAllowed = allowedOrigins.some(allowedOrigin => {
          if (allowedOrigin.includes('localhost') || allowedOrigin.includes('127.0.0.1')) {
            const originBase = event.origin.split(':').slice(0, 2).join(':');
            const allowedBase = allowedOrigin.split(':').slice(0, 2).join(':');
            return originBase === allowedBase;
          }
          return event.origin === allowedOrigin;
        });

        if (!isOriginAllowed) {
          console.warn('🚫 TokenService: Unauthorized origin:', event.origin);
          return;
        }

        if (event.data && event.data.type === 'API_TOKEN') {
          clearTimeout(timeout);
          window.removeEventListener('message', messageHandler);
          console.log('✅ Token received via PostMessage from:', event.origin);
          resolve(event.data.token);
        }
      };

      window.addEventListener('message', messageHandler);

      // Request token from parent
      window.parent.postMessage({
        type: 'REQUEST_TOKEN',
        source: 'FrontEnd',
        timestamp: Date.now()
      }, '*');

      console.log('📤 Token requested from parent via PostMessage');
    });
  }

  /**
   * Method 2: Get token from browser storage
   */
  getTokenFromStorage() {
    // Try sessionStorage first (session-based)
    let token = sessionStorage.getItem('apiToken') || sessionStorage.getItem('crystalApi');
    if (token) {
      console.log('✅ Token found in sessionStorage');
      return Promise.resolve(token);
    }

    // Try localStorage (persistent)
    token = localStorage.getItem('apiToken') || localStorage.getItem('crystalApi');
    if (token) {
      console.log('✅ Token found in localStorage');
      return Promise.resolve(token);
    }

    // Try user object in localStorage (existing pattern)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user && user.token) {
      console.log('✅ Token found in user object (localStorage)');
      return Promise.resolve(user.token);
    }

    return Promise.resolve(null);
  }  /**
  
 * Method 3: Get token from cookies
   */
  getTokenFromCookies() {
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'crystalApi' || name === 'apiToken' || name === 'api-crystal') {
        console.log('✅ Token found in cookies');
        return Promise.resolve(decodeURIComponent(value));
      }
    }

    return Promise.resolve(null);
  }

  /**
   * Method 4: Get token from parent window properties
   */
  getTokenFromParentWindow() {
    try {
      if (window.parent && window.parent !== window) {
        // Try parent window shared data
        if (window.parent.sharedData && window.parent.sharedData.apiToken) {
          console.log('✅ Token found in parent window sharedData');
          return Promise.resolve(window.parent.sharedData.apiToken);
        }

        // Try parent window apiToken property
        if (window.parent.apiToken) {
          console.log('✅ Token found in parent window apiToken');
          return Promise.resolve(window.parent.apiToken);
        }

        // Try parent DOM hidden fields
        if (window.parent.document) {
          const hiddenToken = window.parent.document.getElementById('hiddenApiToken');
          if (hiddenToken && hiddenToken.value) {
            console.log('✅ Token found in parent DOM hidden field');
            return Promise.resolve(hiddenToken.value);
          }
        }
      }
    } catch (error) {
      console.warn('Cannot access parent window (cross-origin):', error.message);
    }

    return Promise.resolve(null);
  }

  /**
   * Method 5: Get token from URL parameters
   */
  getTokenFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token') || urlParams.get('apiToken');
    
    if (token) {
      console.log('✅ Token found in URL parameters');
      // Remove token from URL for security
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      return Promise.resolve(token);
    }

    return Promise.resolve(null);
  }  /**

   * Method 6: Get token from API (encrypt company code and call Node.js API)
   */
  async getTokenFromAPI() {
    try {
      // This would require the RSA encryption logic
      // For now, we'll make a direct call if we have company info
      const companyCode = this.getCompanyCode();
      
      if (!companyCode) {
        return null;
      }

      console.log('🌐 Attempting to get token from API...');
      
      // Note: This would need the encrypted company code
      // For now, return null - implement encryption if needed
      return null;
      
    } catch (error) {
      console.warn('API token retrieval failed:', error.message);
      return null;
    }
  }

  /**
   * Set token and notify listeners
   */
  setToken(token, source = 'unknown') {
    this.token = token;
    this.tokenSource = source;
    
    // Store in localStorage for persistence
    // localStorage.setItem('apiToken', token);
    sessionStorage.setItem('apiToken', token);
    
    // Update user object if it exists
    // const user = JSON.parse(localStorage.getItem('user') || '{}');
    // user.token = token;
    // localStorage.setItem('user', JSON.stringify(user));
    
    console.log(`🎯 Token set from source: ${source}`);
    
    // Notify listeners
    this.listeners.forEach(listener => {
      try {
        listener(token, source);
      } catch (error) {
        console.error('Token listener error:', error);
      }
    });
  }

  /**
   * Get current token
   */
  getToken() {
    return this.token;
  }

  /**
   * Get token source
   */
  getTokenSource() {
    return this.tokenSource;
  } 
   
  /**
   * Add token change listener
   */
  addListener(callback) {
    this.listeners.push(callback);
    
    // If token already exists, call immediately
    if (this.token) {
      callback(this.token, this.tokenSource);
    }
  }

  /**
   * Remove token change listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Clear token
   */
  clearToken() {
    this.token = null;
    this.tokenSource = null;
    
    // Clear from storage
    localStorage.removeItem('apiToken');
    sessionStorage.removeItem('apiToken');
    
    // Clear from user object
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    delete user.token;
    localStorage.setItem('user', JSON.stringify(user));
    
    console.log('🗑️ Token cleared');
    
    // Notify listeners
    this.listeners.forEach(listener => {
      try {
        listener(null, 'cleared');
      } catch (error) {
        console.error('Token listener error:', error);
      }
    });
  }

  /**
   * Refresh token (re-initialize)
   */
  async refreshToken() {
    this.isInitialized = false;
    this.token = null;
    this.tokenSource = null;
    return await this.initialize();
  }  /**
   *
 Get company code from various sources
   */
  getCompanyCode() {
    // Try URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    let companyCode = urlParams.get('company') || urlParams.get('companyCode');
    
    if (companyCode) return companyCode;
    
    // Try localStorage
    companyCode = localStorage.getItem('companyCode');
    if (companyCode) return companyCode;
    
    // Try user object
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.companyCode) return user.companyCode;
    
    // Default company code (if known)
    return 'ABHG48'; // Your default company code
  }

  /**
   * Get authorization header for API calls
   */
  getAuthHeader() {
    if (this.token) {
      return { Authorization: `Bearer ${this.token}` };
    }
    return {};
  }

  /**
   * Check if token is valid (basic check)
   */
  isTokenValid() {
    if (!this.token) return false;
    
    try {
      // Basic JWT validation (check if it has 3 parts)
      const parts = this.token.split('.');
      if (parts.length !== 3) return false;
      
      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      
      return payload.exp ? payload.exp > now : true;
    } catch (error) {
      console.warn('Token validation error:', error);
      return false;
    }
  }
}

// Create singleton instance
const tokenService = new TokenService();

export default tokenService;